import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { emitir } from '../lib/emitir.js';
import { PASOS, type Arranque as EstadoArranque } from '../lib/arranque.js';
import { salir } from '../lib/sesion.js';
import { Campo } from '../ui/Campo.js';
import { DatosDePrueba } from '../ui/DatosDePrueba.js';
import { AvisoCopiaDeTienda } from '../ui/AvisoCopiaDeTienda.js';
import { Idioma } from '../ui/Idioma.js';
import { Chip, Ico, type NombreIcono } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';

/**
 * La pantalla de arranque: una cosa a la vez.
 *
 * A brand-new account has no products, no prices and no open till, and the old home screen
 * rendered exactly as it would on day four hundred — thirteen equal-weight modules and three
 * zeroes. The one obvious button, "Vender", dead-ended on "No hay caja abierta". Somebody who
 * has never used the system could not possibly work out that the order is products, then
 * prices, then the till. The till is the home screen's big button now («Empezar el día»), so the
 * steps end at the prices (the owner, 2026-10-08).
 *
 * So the app says it. One step, why it matters, one button. The steps come from
 * `useArranque`, which reads the actual data, so this cannot claim something is done when it
 * is not, and it disappears by itself the moment the shop can sell.
 *
 * "Saltar por ahora" is deliberate. Somebody who knows what they are doing, or who is just
 * having a look, should never be trapped in a wizard — and the home screen keeps a line
 * pointing back here until it is finished.
 */
/** What each step is about, at a glance, before the sentence is read. */
const ICONO_PASO: Record<string, NombreIcono> = {
  nombre: 'negocio',
  productos: 'productos',
  precios: 'precios',
};

export function Arranque({ estado, onSaltar }: { estado: EstadoArranque; onSaltar: () => void }) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);

  const paso = estado.siguiente;
  if (!paso) return null;

  const numero = PASOS.indexOf(paso.clave) + 1;

  async function ponerNombre() {
    const limpio = nombre.trim();
    if (!limpio) return;
    setGuardando(true);
    try {
      await emitir('config.cambiada', {
        clave: 'negocioNombre',
        valor: limpio,
        anterior: config.negocioNombre,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col p-6">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-400">
            {t('arranque.paso', { n: numero, total: estado.total })}
          </p>
          <div className="mt-2 flex gap-1.5">
            {PASOS.map((clave, i) => (
              <div
                key={clave}
                className={`h-2 flex-1 rounded-full ${
                  estado.pasos[i]?.hecho
                    ? 'bg-marca-500'
                    : i === numero - 1
                      ? 'bg-slate-600'
                      : 'bg-slate-800'
                }`}
              />
            ))}
          </div>
        </div>
        <Idioma tamano="compacto" />
      </header>

      <div className="flex flex-1 flex-col justify-center gap-6 pb-10">
        <div>
          <Chip icono={ICONO_PASO[paso.clave] ?? 'negocio'} tinte="marca" grande />
          <h1 className="mt-5 text-3xl font-bold">{t(`arranque.${paso.clave}.titulo`)}</h1>
          <p className="mt-3 text-slate-400">
            {t(`arranque.${paso.clave}.porque`, { n: paso.faltan })}
          </p>
        </div>

        {paso.clave === 'nombre' ? (
          <div className="flex flex-col gap-4">
            <Campo
              maxLength={60}
              etiqueta={t('comun.nombre')}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={t('arranque.nombre.ejemplo')}
              autoFocus
              autoComplete="off"
              onKeyDown={(e) => e.key === 'Enter' && void ponerNombre()}
            />
            <button
              type="button"
              className="btn-primario h-14 w-full text-lg"
              disabled={!nombre.trim() || guardando}
              onClick={() => void ponerNombre()}
            >
              <Ico icono="guardar" className="size-6" />
              {t(guardando ? 'comun.guardando' : 'arranque.nombre.accion')}
            </button>
          </div>
        ) : paso.clave === 'productos' ? (
          <div className="flex flex-col gap-3">
            <Link to="/productos/semilla" className="btn-primario h-14 w-full text-lg">
              <Ico icono="semilla" className="size-6" />
              {t('arranque.productos.accion')}
            </Link>
            <Link to="/carga-rapida" className="btn-secundario h-12 w-full gap-1.5 text-sm">
              <Ico icono="rapido" className="size-4" />
              {t('arranque.productos.alterna')}
            </Link>
          </div>
        ) : (
          <Link to="/precios" className="btn-primario h-14 w-full text-lg">
            <Ico icono="precios" className="size-6" />
            {t('arranque.precios.accion')}
          </Link>
        )}
      </div>

      <DatosDePrueba />
      <AvisoCopiaDeTienda />

      <button
        type="button"
        className="inline-flex items-center justify-center gap-1.5 py-3 text-sm text-slate-500 underline transition hover:text-slate-300 hover:decoration-2"
        onClick={onSaltar}
      >
        <Ico icono="saltar" className="size-4" />
        {t('arranque.saltar')}
      </button>
    </div>
  );
}

/**
 * Shown to staff who cannot finish the setup themselves, instead of a home screen that lies.
 *
 * With a way out: it tells them to come back once the owner has finished, and the owner is often
 * standing right there, on this same device. Without it a cashier's sign-in held the device
 * until the lock came fifteen minutes later.
 */
export function Esperando() {
  const t = useT();
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-xl font-semibold">{t('arranque.esperando.titulo')}</h1>
      <p className="text-sm text-slate-400">{t('arranque.esperando.porque')}</p>
      <button type="button" className="btn-secundario" onClick={() => void salir()}>
        <Ico icono="salir" />
        {t('entrar.cambiarUsuario')}
      </button>
    </div>
  );
}
